'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { IconButton, Tooltip, Text } from '@terra/ui-design-system';
import { FlashIcon } from '@terra/ui-icons';
import { useProxyStore } from '../../store/proxy-store';

type InterceptMode = 'off' | 'requests' | 'responses' | 'all';

function getInterceptMode({
  interceptEnabled,
  interceptResponsesEnabled,
}: {
  interceptEnabled: boolean;
  interceptResponsesEnabled: boolean;
}): InterceptMode {
  if (interceptEnabled && interceptResponsesEnabled) {
    return 'all';
  }

  if (interceptEnabled) {
    return 'requests';
  }

  if (interceptResponsesEnabled) {
    return 'responses';
  }

  return 'off';
}

function getInterceptLabel(mode: InterceptMode): string {
  switch (mode) {
    case 'all':
      return 'Intercepting All';
    case 'requests':
      return 'Intercepting Requests';
    case 'responses':
      return 'Intercepting Responses';
    default:
      return 'Intercept Off';
  }
}

function getNextModeLabel(mode: InterceptMode): string {
  switch (mode) {
    case 'off':
      return 'Click to intercept requests';
    case 'requests':
      return 'Click to intercept responses';
    case 'responses':
      return 'Click to intercept all';
    case 'all':
      return 'Click to disable intercept';
  }
}

export function TopBarInterceptIndicator() {
  const interceptEnabled = useProxyStore(
    (state) => state.settings.interceptEnabled,
  );
  const interceptResponsesEnabled = useProxyStore(
    (state) => state.settings.interceptResponsesEnabled,
  );
  const updateSettings = useProxyStore((state) => state.updateSettings);
  const queueCount = useProxyStore((state) => state.interceptQueue.length);
  const interceptMode = getInterceptMode({
    interceptEnabled,
    interceptResponsesEnabled,
  });
  const isActive = interceptMode !== 'off';
  const handleToggle = () => {
    switch (interceptMode) {
      case 'off':
        // Off → Requests
        updateSettings({
          interceptEnabled: true,
          interceptResponsesEnabled: false,
        });
        break;
      case 'requests':
        // Requests → Responses
        updateSettings({
          interceptEnabled: false,
          interceptResponsesEnabled: true,
        });
        break;
      case 'responses':
        // Responses → All
        updateSettings({
          interceptEnabled: true,
          interceptResponsesEnabled: true,
        });
        break;
      case 'all':
        // All → Off
        updateSettings({
          interceptEnabled: false,
          interceptResponsesEnabled: false,
        });
        break;
    }
  };

  const tooltipContent = (
    <VStack gap={1} alignItems="flex-start" p={1}>
      <Text fontSize="xs" fontWeight="medium">
        {getInterceptLabel(interceptMode)}
      </Text>
      <Text fontSize="2xs" color="fg.muted">
        {getNextModeLabel(interceptMode)}
      </Text>
      {queueCount > 0 && (
        <Text fontSize="2xs" color="fg.muted">
          {queueCount} item{queueCount !== 1 ? 's' : ''} in queue
        </Text>
      )}
    </VStack>
  );

  return (
    <Tooltip
      openDelay={50}
      positioning={{
        placement: 'bottom',
        strategy: 'fixed',
      }}
      customContent={tooltipContent}
    >
      <IconButton
        variant="ghost"
        size="xs"
        aria-label="Toggle intercept mode"
        onClick={handleToggle}
        position="relative"
      >
        <FlashIcon
          size={16}
          strokeWidth={1.75}
          style={{
            color: isActive ? 'var(--colors-orange-9)' : undefined,
          }}
        />
        {isActive && (
          <Box
            position="absolute"
            top="0"
            right="0"
            w="6px"
            h="6px"
            borderRadius="full"
            bg="orange.9"
          />
        )}
        {queueCount > 0 && (
          <HStack
            position="absolute"
            bottom="-2px"
            right="-4px"
            bg="orange.9"
            borderRadius="full"
            minW="14px"
            h="14px"
            px="1"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="2xs" fontWeight="bold" color="white">
              {queueCount > 99 ? '99+' : queueCount}
            </Text>
          </HStack>
        )}
      </IconButton>
    </Tooltip>
  );
}
